import React from 'react';
import { useParams } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { Avatar, Spinner, Tabs } from '@heroui/react';
import { NavBar } from '@/components/layout';
import { IconChartBar as ChartBar } from '@tabler/icons-react';

/**
 * 活动统计页面
 * 
 * 路由模式：/activities/:id/stats
 */
export default function ActivityStatsPage() {
  const { id: activityId } = useParams<{ id: string }>();
  const [selectedTab, setSelectedTab] = React.useState<string>('all');

  // 查询所有 BRANCH 类型的组织（parentId 为 null）
  const { data: orgs, isLoading: orgsLoading } = trpc.orgs.listAll.useQuery(
    {
      type: 'BRANCH',
      parentId: null,
    },
    {
      enabled: !!activityId,
    }
  );

  // 根据选中的 tab 确定查询参数
  const statsQueryParams = React.useMemo(() => {
    if (selectedTab === 'all') {
      // "全部"：unitType="SPACE"，unitId 为空（后端自动注入空间ID）
      return {
        activityId: activityId!,
        unitType: 'SPACE' as const,
        // unitId 不传，后端会自动注入空间ID
      };
    } else {
      // 组织：unitType="ORG"，unitId 为实际的 orgId
      return {
        activityId: activityId!,
        unitType: 'ORG' as const,
        unitId: selectedTab,
      };
    }
  }, [activityId, selectedTab]);

  // 查询基础统计
  const { data: basicStats, isLoading: statsLoading } = trpc.activities.getBasicStats.useQuery(
    statsQueryParams,
    {
      enabled: !!activityId,
    }
  );

  // 查询排行榜（第1页，每页10人）
  const { data: leaderboard, isLoading: leaderboardLoading } = trpc.activities.getInvitationLeaderboard.useQuery(
    {
      ...statsQueryParams,
      page: 1,
      pageSize: 10,
    },
    {
      enabled: !!activityId,
    }
  );

  const contentLoading = statsLoading || leaderboardLoading;
  const topThreePodiumItems = React.useMemo(() => {
    if (!leaderboard?.items?.length) return [];

    return leaderboard.items
      .filter((item) => item.rank >= 1 && item.rank <= 3)
      .sort((a, b) => a.rank - b.rank)
      .map((item) => {
        const displayName = item.person?.fullName ?? item.user.nickname ?? '未知用户';
        return {
          rank: item.rank as 1 | 2 | 3,
          avatar: item.user.avatarUrl ?? '',
          name: displayName,
          unit: '邀请（人）',
          value: item.inviteCount,
        };
      });
  }, [leaderboard]);
  const rankedListItems = React.useMemo(
    () => leaderboard?.items.filter((item) => item.rank >= 4 && item.rank <= 10) ?? [],
    [leaderboard]
  );

  return (
    <>
      <NavBar
        title="活动统计"
        showBack
      />
      <div className="w-full pt-[var(--navbar-height,44px)]">
        <div className="p-4 space-y-6">
          {/* 第一部分：Tabs */}
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <Tabs.ListContainer>
              <Tabs.List className="w-full justify-start overflow-x-auto">
              <Tabs.Tab id="all">全部</Tabs.Tab>
              {!orgsLoading && orgs?.map((org) => (
                <Tabs.Tab key={org.id} id={org.id}>
                  {org.shortName || org.fullName || '未命名'}
                </Tabs.Tab>
              ))}
              </Tabs.List>
            </Tabs.ListContainer>

            {/* 第二部分：基础统计 */}
            <Tabs.Panel id={selectedTab} className="mt-4">
              {contentLoading ? (
                <div className="flex items-center justify-center min-h-[40vh]">
                  <Spinner className="w-8 h-8" />
                </div>
              ) : (
                <>
                {basicStats && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-content1 rounded-lg p-4 text-center border border-default-200">
                      <div className="text-2xl font-bold text-foreground mb-1">
                        {basicStats.participantCount}
                      </div>
                      <div className="text-sm text-default-500">参与者人数</div>
                    </div>
                    <div className="bg-content1 rounded-lg p-4 text-center border border-default-200">
                      <div className="text-2xl font-bold text-foreground mb-1">
                        {basicStats.inviterCount}
                      </div>
                      <div className="text-sm text-default-500">邀请者人数</div>
                    </div>
                    <div className="bg-content1 rounded-lg p-4 text-center border border-default-200">
                      <div className="text-2xl font-bold text-foreground mb-1">
                        {basicStats.averageInvites}
                      </div>
                      <div className="text-sm text-default-500">人均邀约</div>
                    </div>
                  </div>
                )}

                {/* 第三部分：排行榜 */}
                {leaderboard && leaderboard.items.length > 0 && (
                  <div className="mt-6">
                    <div className="mb-4 flex items-center justify-center gap-2">
                      <ChartBar className="w-5 h-5 text-default-500" />
                      <h3 className="text-base font-semibold">邀请排行榜</h3>
                    </div>
                    <div className="space-y-2">
                      {topThreePodiumItems.length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {topThreePodiumItems.map((item) => (
                            <div key={item.rank} className="rounded-lg border border-default-200 bg-content1 p-3 text-center">
                              <p className="text-xs text-default-500">第{item.rank}名</p>
                              <p className="mt-1 truncate text-sm font-medium">{item.name}</p>
                              <p className="text-xs text-default-500">{item.value}人</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {rankedListItems.map((item) => (
                        <div
                          key={item.inviterId}
                          className="flex items-center gap-3 p-3 bg-content1 rounded-lg border border-default-200"
                        >
                          <div className="flex-shrink-0 w-8 text-center">
                            <span className="text-sm font-semibold text-default-500">
                              {item.rank}
                            </span>
                          </div>
                          <Avatar className="w-10 h-10">
                            {item.user.avatarUrl ? (
                              <Avatar.Image
                                src={item.user.avatarUrl}
                                alt={item.user.nickname || '用户'}
                              />
                            ) : null}
                            <Avatar.Fallback>
                              {(item.person?.fullName ?? item.user.nickname ?? '?')
                                .charAt(0)
                                .toUpperCase()}
                            </Avatar.Fallback>
                          </Avatar>
                          <div className="flex-1 min-w-0 flex flex-col">
                            <p className="text-sm font-medium text-foreground truncate">
                              {item.person?.fullName ?? item.user.nickname ?? '未知用户'}
                            </p>
                            {item.person?.jobNumber != null && (
                              <p className="text-xs text-default-500 truncate">
                                {item.person.jobNumber}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <span className="text-sm text-default-500">
                              {item.inviteCount}人
                            </span>
                          </div>
                        </div>
                      ))}
                      {rankedListItems.length === 0 && (
                        <div className="py-6 text-center">
                          <p className="text-sm text-default-500">暂无第4-10名数据</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 空状态 */}
                {leaderboard && leaderboard.items.length === 0 && (
                  <div className="mt-6 text-center py-8">
                    <p className="text-sm text-default-500">暂无排行榜数据</p>
                  </div>
                )}
                </>
              )}
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    </>
  );
}
